import { queryOptions } from "@tanstack/solid-query";
import { axiosClient } from "~/js/network/axiosClient";
import type {
	IWorkspaceListItem,
	IWorkspaceListItemLight,
	IWorkspaceTemplate,
	IWorkspaceWithFiles
} from "~/solidJs/entities/WorkspaceListEntity/helpers/workspaceTypes";
import { ApiErrorHandlers, BaseApi } from "~/solidJs/shared/api/baseApi";

export class WorkspaceApi extends BaseApi {
	static baseUrl = `${BaseApi.baseUrlV2}/workspaces`;
	static workspaceTemplateUrl = `${BaseApi.baseUrlV2}/workspaces/workspace_template`;
	static adminWorkspaceTemplateUrl = `${BaseApi.baseUrlV2}/admin/workspace_template`;
	static workspaceListLightKey = "workspaceLightList";

	/**
	 * @description get workspace list without configs
	 */
	static async getWorkspaceLightList() {
		const { data } = await axiosClient.axiosClient.get<IWorkspaceListItemLight[]>(
			`${WorkspaceApi.baseUrl}/light`
		);
		return data;
	}

	/**
	 * @description get workspace config by id
	 */
	static async getWorkspaceConfigById(id: number | string) {
		const { data } = await axiosClient.axiosClient.get<IWorkspaceListItem>(
			`${WorkspaceApi.baseUrl}/by_id/${id}`
		);
		return data;
	}

	/**
	 * @description new method for creating new workspace with files ids list
	 */
	static async postWorkspaceWithFileMove(
		workspaceWithFilesIds: IWorkspaceWithFiles,
		name = "Untitled"
	) {
		const { data } = await axiosClient.axiosClient.post<IWorkspaceListItem>(
			`${WorkspaceApi.baseUrl}/save`,
			{ ...workspaceWithFilesIds, name }
		);
		return data;
	}

	/**
	 * @description new method for updating workspace with files ids list
	 */
	static async putWorkspaceWithFileMove(workspaceWithFilesIds: IWorkspaceWithFiles, id: number) {
		const { data } = await axiosClient.axiosClient.post<IWorkspaceListItem>(
			`${WorkspaceApi.baseUrl}/update/${id}`,
			workspaceWithFilesIds
		);
		return data;
	}

	static async deleteWorkspaceById(id: number) {
		const { data } = await axiosClient.axiosClient.delete(
			`${WorkspaceApi.baseUrl}/by_id/${id}`
		);
		return data;
	}

	/**
	 * rename workspace and returns it config
	 * @param id
	 * @param newName
	 * @returns {IWorkspaceListItem}
	 */
	static async renameWorkspaceById(id: number, newName: string) {
		const { data } = await axiosClient.axiosClient.post<IWorkspaceListItem>(
			`${WorkspaceApi.baseUrl}/rename/${id}`,
			{},
			{
				params: {
					name: newName
				}
			}
		);
		return data;
	}

	/**
	 * @description create workspace link. if link already exist, returns an old one
	 * @example returns {workspaceLink: "F8AB0F316CC6A95982C806938A3DE9B0"}
	 */
	static async createWorkspaceLink(workspaceId: number) {
		const { data } = await axiosClient.axiosClient.post<IWorkspaceListItem>(
			`${WorkspaceApi.baseUrl}/share/${workspaceId}`
		);
		return data;
	}

	/**
	 * @description unshare workspace
	 */
	static async deleteWorkspaceLink(workspaceId: number) {
		const _response = await axiosClient.axiosClient.delete(
			`${WorkspaceApi.baseUrl}/share/${workspaceId}`
		);
	}

	/**
	 * @description get workspace data by link
	 */
	static async getWorkspaceByLink(workspaceUUID: string) {
		const response = await axiosClient.axiosClient.get<IWorkspaceListItem>(
			`${WorkspaceApi.baseUrl}/shared/${workspaceUUID}`
		);
		return response.data;
	}

	static getWorkspaceTemplatesOptions() {
		return queryOptions({
			queryKey: ["getWorkspaceTemplates"],
			queryFn: () => WorkspaceApi.getWorkspaceTemplates()
		});
	}
	/**
	 * @description returns list of all templates
	 */
	static async getWorkspaceTemplates() {
		const response = await axiosClient.axiosClient.get<IWorkspaceTemplate[]>(
			WorkspaceApi.workspaceTemplateUrl,
			{}
		);

		return response.data;
	}
	/**
	 * @description returns onboarding template by reserved id
	 */
	static async getOnboardingTemplate(id: number) {
		const response = await axiosClient.axiosClient.get<IWorkspaceTemplate[]>(
			WorkspaceApi.workspaceTemplateUrl,
			{
				params: {
					template_id: id
				}
			}
		);

		return response.data[0];
	}

	/**
	 * creates template
	 */
	static async createWorkspaceTemplate(name: string, jsonData: IWorkspaceWithFiles) {
		try {
			await axiosClient.axiosClient.post(WorkspaceApi.adminWorkspaceTemplateUrl, {
				name: name,
				payload: jsonData.config,
				uploaded_file_ids: jsonData.uploaded_file_ids
			});
		} catch (error) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(error);
		}
	}
	/**
	 * updates specific template
	 */
	static async updateWorkspaceTemplate(name: string, jsonData: IWorkspaceWithFiles) {
		await axiosClient.axiosClient.patch(WorkspaceApi.adminWorkspaceTemplateUrl, {
			name: name,
			payload: jsonData.config,
			uploaded_file_ids: jsonData.uploaded_file_ids
		});
	}
}
